import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Footer-only pagination control matching Probe's monochrome aesthetic.
 *
 *   Showing 1–50 of 234   ‹ Prev   1 2 [3] 4 5   Next ›
 *
 * Props:
 *   page         current 1-based page index
 *   perPage      items per page
 *   total        total record count across all pages
 *   onChange     (newPage) => void
 *   testId       data-testid prefix; pagination root gets `${testId}-root`
 */
export function Pagination({ page, perPage, total, onChange, testId = "pagination" }) {
  if (!total) return null;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  if (totalPages < 2) return null;

  const start = (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, total);

  // Page-number window: show up to 5 numbers around the current page.
  const window = 2;
  const from = Math.max(1, page - window);
  const to = Math.min(totalPages, page + window);
  const pages = [];
  for (let p = from; p <= to; p += 1) pages.push(p);

  const go = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    onChange(p);
  };

  return (
    <div
      data-testid={`${testId}-root`}
      className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-t border-[#E5E7EB] bg-white px-3 sm:px-4 py-3"
    >
      <p className="font-mono text-[11px] tracking-[0.12em] text-[#6B7280]" data-testid={`${testId}-status`}>
        Showing <span className="text-black font-semibold">{start}</span>
        <span className="mx-0.5">–</span>
        <span className="text-black font-semibold">{end}</span> of{" "}
        <span className="text-black font-semibold">{total}</span>
      </p>
      <nav className="inline-flex items-stretch border border-[#D1D5DB]" aria-label="Pagination">
        <button
          type="button"
          onClick={() => go(page - 1)}
          disabled={page <= 1}
          data-testid={`${testId}-prev`}
          className="inline-flex items-center gap-1 px-3 h-9 text-[11px] font-semibold tracking-[0.12em] border-r border-[#D1D5DB] bg-white hover:bg-[#F3F4F6] disabled:opacity-40 disabled:hover:bg-white disabled:cursor-not-allowed"
        >
          <ChevronLeft size={12} /> PREV
        </button>
        {from > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(1)}
              data-testid={`${testId}-page-1`}
              className="px-3 h-9 text-[11px] font-mono border-r border-[#D1D5DB] bg-white hover:bg-[#F3F4F6]"
            >
              1
            </button>
            {from > 2 && (
              <span className="px-2 h-9 inline-flex items-center text-[11px] font-mono text-[#9CA3AF] border-r border-[#D1D5DB] bg-white select-none">
                …
              </span>
            )}
          </>
        )}
        {pages.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => go(p)}
            data-testid={`${testId}-page-${p}`}
            aria-current={p === page ? "page" : undefined}
            className={`px-3 h-9 text-[11px] font-mono border-r border-[#D1D5DB] last:border-r-0 ${
              p === page
                ? "bg-black text-white"
                : "bg-white text-black hover:bg-[#F3F4F6]"
            }`}
          >
            {p}
          </button>
        ))}
        {to < totalPages && (
          <>
            {to < totalPages - 1 && (
              <span className="px-2 h-9 inline-flex items-center text-[11px] font-mono text-[#9CA3AF] border-l border-[#D1D5DB] bg-white select-none">
                …
              </span>
            )}
            <button
              type="button"
              onClick={() => go(totalPages)}
              data-testid={`${testId}-page-${totalPages}`}
              className="px-3 h-9 text-[11px] font-mono border-l border-[#D1D5DB] bg-white hover:bg-[#F3F4F6]"
            >
              {totalPages}
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => go(page + 1)}
          disabled={page >= totalPages}
          data-testid={`${testId}-next`}
          className="inline-flex items-center gap-1 px-3 h-9 text-[11px] font-semibold tracking-[0.12em] border-l border-[#D1D5DB] bg-white hover:bg-[#F3F4F6] disabled:opacity-40 disabled:hover:bg-white disabled:cursor-not-allowed"
        >
          NEXT <ChevronRight size={12} />
        </button>
      </nav>
    </div>
  );
}
